import * as React from 'react';
import { DataSignal } from 's-js';
import * as cx from 'classnames';
import SuperCritical from 'lib/supercritical';
import { tzmoment } from 'lib/tzmoment';
import UOM from 'lib/UOM';
import { noDefault } from 'lib/eventWrappers';

import { PilotShiphandlingNote } from 'data/models/PilotShiphandlingNote';

import {
    PilotJobOrderDetails,
    BackPage,
    ItineraryPointViewModel,
    VesselViewModel,
    EffectiveDraftViewModel,
    CancellationRemarksPrompt,
    AbortRemarksPrompt,
    CancelWholePortCallPrompt,
    CancelWholePortCallStatus
} from './model';

import { ActionLoadingIndicator } from 'core/actionloadingindicator/view';
import { AddShiftPopupView } from 'pilots/addshiftpopup/view';
import { EditMovementEndPopupView } from 'portcall/editmovementendpopup/view';
import {
    DateTimeEditPopupView,
    DateTimeEditPopupTrigger,
    NullableDateTimeEditPopupView,
    NullableDateTimeEditPopupTrigger
} from 'portcall/datetimeeditpopup/view';
import { ErrorModal } from 'core/validationerror/view';
import { FeetInchesMetersEditView } from 'portcall/feetinchesmetersedit/view';
import { FootmarkAssignmentsView } from 'portcall/footmarkassignments/view';
import { IssuesShortSummary } from 'portcall/issues/view';
import { NumericInput, NullableNumericInput } from 'portcall/numericinput/view';
import { PCDismissible } from 'portcall/pcdismissible/view';
import { PCModal } from 'portcall/pcmodal/view';
import { PilotJobOrderLogView } from 'pilots/pilotjoborderlog/view';
import { SmallConflictCountBadge } from 'portcall/issues/view';
import { TimeAgoTooltip } from 'portcall/pctooltip/view';
import { TypeaheadInput } from 'portcall/typeahead/view';

import { PilotAssignmentsView } from './pilotassignmentsview';
import { PilotageChargesView } from './pilotagechargesview';
import { PrintOriginalOrderLink, OriginalPilotOrderView } from './originalpilotorderview';

export const PilotJobOrderDetailsView = SuperCritical(({ modelContainer } : { modelContainer : () => PilotJobOrderDetails | null }) => {
    const m = modelContainer();
    if (!m) return null;
    return (
        <div className="pilotjoborderdetails pc-card">
            {/*<loadingindicator params="request: Request" />*/}
            <div className='details-content'>
                <DetailsHeader model={m} />
                <DetailsBody model={m} />
                <ActionLoadingIndicator loading={m.JobOrder.Working} />
            </div>
        </div>
    );
});

const DetailsHeader = SuperCritical(({ model: m } : { model : PilotJobOrderDetails }) =>
    <div className="details-header pc-card-header">
        <ActionsContainer model={m} />
        <ActionConfirmation model={m} />
        <DetailsTitle model={m} />
    </div>
);

const ActionsContainer = SuperCritical(({ model: m } : { model : PilotJobOrderDetails }) =>
    <div className="pjo-actions-container">
        {m.JobOrder.Model.AcknowledgedDate() === null && m.CanAcknowledge() &&
            <a href="" className="pjo-acknowledge-link" onClick={noDefault(m.JobOrder.Acknowledge)}>Acknowledge</a>
        }
        <button className="pjo-actions-menu-btn fa fa-bars" onClick={m.ToggleActionsMenu}></button>
        {m.ShowActionsMenu() &&
            <PCDismissible className="pjo-actions-menu" onDismiss={() => m.ShowActionsMenu(false)}>
                <BerthTimelineLink model={m} />
                <PortCallProfileLink model={m} />
                <FlipToLogLink model={m} />
                <PrintOriginalOrderLink model={m.OriginalPilotOrder} />
                <CancelPJOLink model={m} />
                <ReorderPJOLink model={m} />
                <AbortPJOLink model={m} />
                <DeletePJOLink model={m} />
            </PCDismissible>
        }
    </div>
);

const BerthTimelineLink = SuperCritical(({ model: m } : { model : PilotJobOrderDetails }) => {
    const point =
        m.JobOrder.FromPoint()?.BerthReservation() ? m.JobOrder.FromPoint()! :
        m.JobOrder.ToPoint  ()?.BerthReservation() ? m.JobOrder.ToPoint  ()! :
        null;
    if (!point) return null;
    return (
        <div className="pjo-actions-menu-item">
            <a href={'#!schedulegrid'
                    + '?date=' + tzmoment(point.Model.Arrival.Effective()).add(-1, 'days').format('YYYY-MM-DD')
                    + '&resid=' + point.BerthReservation()!.Model.Id()
            }>Go to Berths Timeline</a>
        </div>
    );
});

const PortCallProfileLink = SuperCritical(({ model: m } : { model : PilotJobOrderDetails }) =>
    <div className="pjo-actions-menu-item">
        <a href={'#!portcallprofile?id=' + m.JobOrder.Model.PortCallId()}>
            Go to the PortCall Profile
        </a>
    </div>
);

const FlipToLogLink = SuperCritical(({ model: m } : { model : PilotJobOrderDetails }) =>
    <div className="pjo-actions-menu-item">
        <a href="" onClick={noDefault(m.MenuAction(() => m.FlipToBack(BackPage.Log)))}>Flip to Log</a>
    </div>
);

const CancelPJOLink = SuperCritical(({ model: m } : { model : PilotJobOrderDetails }) =>
    !m.JobOrder.Model.Start.Actual() && m.Model.CanCancel() && !m.JobOrder.Model.Cancellation() ?
        <div className="pjo-actions-menu-item">
            <a href="" onClick={noDefault(m.MenuAction(() => m.ActionToConfirm(m.JobOrder.PromptForCancellationRemarks)))}>Cancel Movement</a>
        </div> : null
);

const ReorderPJOLink = SuperCritical(({ model: m } : { model : PilotJobOrderDetails }) =>
    m.CanReorder() && m.JobOrder.Model.Cancellation() ?
        <div className="pjo-actions-menu-item">
            <a href="" onClick={noDefault(m.MenuAction(m.JobOrder.Reorder))}>Reorder Movement</a>
        </div> : null
);

const AbortPJOLink = SuperCritical(({ model: m } : { model : PilotJobOrderDetails }) =>
    m.JobOrder.Model.Start.Actual() && m.Model.CanCancel() && !m.JobOrder.Model.Cancellation() ?
        <div className="pjo-actions-menu-item">
            <a href="" onClick={noDefault(m.MenuAction(() => m.ActionToConfirm(m.JobOrder.PromptForAbortRemarks)))}>Abort Movement</a>
        </div> : null
);

const DeletePJOLink = SuperCritical(({ model: m } : { model : PilotJobOrderDetails }) =>
    m.Model.CanDelete() ?
        <div className="pjo-actions-menu-item">
            <a href="" onClick={noDefault(m.MenuAction(() => m.ActionToConfirm(m.JobOrder.Delete)))}>Delete Movement</a>
        </div> : null
);

const ActionConfirmation = SuperCritical(({ model: m } : { model : PilotJobOrderDetails }) =>
    m.ActionToConfirm() &&
        <div className="pjo-details-action-conf">
            <span>Are you sure?</span>
            {' '}
            <a className="pjo-details-action-conf-yes" href="" onClick={noDefault(m.ActionToConfirm()!)}>Yes</a>
            {' '}
            <a className="pjo-details-action-conf-no"  href="" onClick={noDefault(() => m.ActionToConfirm(null))}>No</a>
        </div>
);

const DetailsTitle = SuperCritical(({ model: m } : { model : PilotJobOrderDetails }) => 
    <div className="pc-card-title">
        <span className="vessel">{m.JobOrder.Model.DisplayVesselIdentifier()}</span>
        {' '}
        <span className="em-dash">&mdash;</span>
        {' '}
        <LocationNameView point={m.JobOrder.FromPoint} />
        {' '}
        <span className="location is-not-berth">to</span>
        {' '}
        <LocationNameView point={m.JobOrder.ToPoint} />
        {' '}
        <span className="em-dash">&mdash;</span>
        {' '}
        <span className="date">{tzmoment(m.JobOrder.Model.Start.Effective()).format('MMMM DD, YYYY')}</span>
        {' '}
        {m.JobOrder.Model.Cancellation() &&
            <span>
                <span className="em-dash">&mdash;</span>
                {' '}
                <span className="cancelled-msg">Cancelled</span>
            </span>
        }
        {m.JobOrder.PortCall()?.Unlisted() ? <span> &mdash; UNLISTED</span> : ''}
        <span className="print-reminder">*remember to set your page range</span>
    </div>
);

const LocationNameView = SuperCritical(({ point: pt } : { point : () => ItineraryPointViewModel | null }) =>
    <span className={cx('location', {
            'is-not-berth':
                !pt() ||
                !pt()!.Model.Location() ||
                !pt()!.Model.Location()!.Berth()
            })}>
        {pt() && pt()!.Model.Location() && pt()!.Model.Location()!.Name()}
    </span>
);


const DetailsBody = SuperCritical(({ model: m } : { model : PilotJobOrderDetails }) =>
    <div className="details-body flip-card">
        <DetailsFrontContent model={m} />
        <DetailsBackContent model={m} />
    </div>
);

const DetailsFrontContent = SuperCritical(({ model: m } : { model : PilotJobOrderDetails }) =>
    <div className={cx('face front', { flipped: m.Flipped() })}>
        <DetailsBodyLeft model={m} />
        <DetailsBodyRight model={m} />
    </div>
);

const DetailsBodyLeft = SuperCritical(({ model: m } : { model : PilotJobOrderDetails }) =>
    <div className="details-body-left details-body-column">
        <MovementTimes model={m} />
        <div className="space-allocation pilots">
            <PilotAssignmentsView model={m.PilotAssignments} />
        </div>
        <BerthReservationDetails model={m} />
        <ServicesView model={m} />
    </div>
);

const MovementTimes = SuperCritical(({ model: m } : { model : PilotJobOrderDetails }) => {
    const
        fromRes = m.JobOrder.FromPoint() && m.JobOrder.FromPoint()!.BerthReservation(),
        toRes   = m.JobOrder.ToPoint  () && m.JobOrder.ToPoint  ()!.BerthReservation();
    return (
        // TH 02/06/2017 -- Changing movement-times to pjo-movement-times to prevent
        // styles from the new MovementTimes widget (portcall/movementtimes/*) from bleeding
        // through.
        <div className="details-section pjo-movement-times">
            <div className="details-section-header">
                <span className="details-section-title">Movement Times</span>
            </div>
            <div className={cx('details-section-body mvmt-times-body', {
                    'show-mvmt-in': !fromRes && !!toRes,
                    'show-mvmt-out': !!fromRes && !toRes,
                    'show-mvmt-shift': !!fromRes && !!toRes
                })}>
                <StartTimes model={m} />
                <EndTimes model={m} />
                <LogStartActualLink model={m} />
                <LogEndActualLink model={m} />
                <UndoStartActualConfirmation model={m} />
                <UndoEndActualConfirmation model={m} />
                <PrimaryBerthLabel model={m} />
                <SecondaryLocationLabel model={m} />
                <TransitLine model={m} />
                {/*"Alignment" ticks used to visually align datetimepickers w/ transit duration line*/}
                <div className="alignment-tick alignment-tick-start"></div>
                <div className="alignment-tick alignment-tick-end"></div>
            </div>
        </div>
    );
});

// Start Est. and Actual, Start Time Confirmed Toggle, Undo Start Actual Link/Icon
const StartTimes = SuperCritical(({ model: m } : { model : PilotJobOrderDetails }) =>
    <div className="start-times">
        <StartEstimate model={m} />
        <StartActual model={m} />
    </div>
);

const StartEstimate = SuperCritical(({ model: m } : { model : PilotJobOrderDetails }) =>
    !m.JobOrder.Model.Start.Actual() ?
        <div className="start-estimate">
            <div className="mvmt-time-label">
                <span className="mvmt-in">Arriving</span>
                <span className="mvmt-out">Departing</span>
                <span className="mvmt-shift">Shifting</span>
            </div>
            <div>
                <DateTimeEditPopupTrigger
                    value={m.JobOrder.SyncedStartAndEndEstimate}
                    disabled={!m.Model.CanEditTimes() || !!m.JobOrder.Model.Cancellation()}
                    onTriggerPopup={() => m.JobOrder.ShowDateTimeEditPopup('start-est')}
                    localization={m.Localization}
                />
                <StartTimeConfirmation model={m} />
            </div>
        </div> : null
);

const StartTimeConfirmation = SuperCritical(({ model: m } : { model : PilotJobOrderDetails }) => {
    const className = cx('start-time-confirmation pjo-details-confirmation', {
            'pjo-details-confirmed': m.JobOrder.Model.StartTimeConfirmed()
        }),
        classSpan = 'start-time-confirmation-span',
        text = m.JobOrder.Model.StartTimeConfirmed() ? 'Firm' : 'Tentative';
    return m.Model.CanEditStartTimeConfirmed() && !m.JobOrder.Model.Cancellation() ?
        <a href="" className={className}
            onClick={noDefault(m.JobOrder.ToggleStartConfirmed)}>{text}</a> :
        <span className={classSpan}>{text}</span>
});

const StartActual = SuperCritical(({ model: m } : { model : PilotJobOrderDetails }) =>
    m.JobOrder.Model.Start.Actual() && !m.JobOrder.ShowUndoStartActual() ?
        <div className="start-actual">
            <div className="mvmt-time-label">
                <span className="mvmt-in">Arrived</span>
                <span className="mvmt-out">Departed</span>
                <span className="mvmt-shift">Shifted</span>
            </div>
            <div>
                <DateTimeEditPopupTrigger
                    value={m.JobOrder.Model.Start.Effective}
                    disabled={!m.Model.CanEditTimes() || !!m.JobOrder.Model.Cancellation()}
                    onTriggerPopup={() => m.JobOrder.ShowDateTimeEditPopup('start-effective')}
                    localization={m.Localization}
                />
                {/* TH 03/09/2017 -- we'll also set PJO.Vessel and PJO.Length when undoing pilot aboard.
                    So check CanChangeVessel in addition to CanEditTimes.
                    This is really just defensive since the two permissions currently check the same roles. */}
                {m.Model.CanEditTimes() && m.Model.CanChangeVessel() && !m.JobOrder.Model.Cancellation() && !m.JobOrder.Model.End.Actual() &&
                    <i className="undo-btn icon-undo-circle"
                        onClick={m.JobOrder.ShowUndoStartActual}></i>
                }
            </div>
        </div> : null
);

// End Est. and Actual, Undo End Actual Link/Icon
// TH: Still very apocalyptic :(
const EndTimes = SuperCritical(({ model: m } : { model : PilotJobOrderDetails }) =>
    <div className="end-times">
        <EndEstimate model={m} />
        <EndActual model={m} />
    </div>
);

const EndEstimate = SuperCritical(({ model: m } : { model : PilotJobOrderDetails }) => {
    if (m.JobOrder.Model.End.Actual()) return null;
    const endEstimateText = tzmoment(m.JobOrder.Model.End.Estimated()).format('HH:mm');
    return (
        <div className="end-estimate">
            <span className="end-estimate-descr pc-text-fixedwidth">
                <span className="mvmt-in">Est. First Line at</span>
                <span className="mvmt-out">Est. Pilot End (Disembark) at</span>
                <span className="mvmt-shift">
                    Est. First Line
                    {' '}
                    {m.JobOrder.ToPoint()?.Model.Location()?.Name()}
                    {' '}
                    at
                </span>
            </span>
            {' '}
            {/* 05/17/2017 DC -- temp fix for times until we can sort out the relationships */}
            {m.Model.CanEditTimes() && !m.JobOrder.Model.Cancellation() && !m.JobOrder.Model.Start.Actual() ?
                <a href="" className="end-estimate-link"
                    onClick={noDefault(() => m.JobOrder.ShowEditMovementEndPopup(true))}>
                    {endEstimateText}
                </a> :
                <span>{endEstimateText}</span>
            }
        </div>
    );
});

const EndActual = SuperCritical(({ model: m } : { model : PilotJobOrderDetails }) =>
    m.JobOrder.Model.End.Actual() && !m.JobOrder.ShowUndoEndActual() ?
        <div className="end-actual">
            <span className="mvmt-time-label">Pilot Complete</span>
            <DateTimeEditPopupTrigger
                value={m.JobOrder.Model.End.Effective}
                disabled={!m.Model.CanEditTimes() || !!m.JobOrder.Model.Cancellation()}
                onTriggerPopup={() => m.JobOrder.ShowDateTimeEditPopup('end-effective')}
                localization={m.Localization}
            />
            {m.Model.CanEditTimes() && !m.JobOrder.Model.Cancellation() &&
                <i className="undo-btn icon-undo-circle"
                    onClick={() => m.JobOrder.ShowUndoEndActual(true)}></i>
            }
        </div> : null
);

const LogStartActualLink = SuperCritical(({ model: m } : { model : PilotJobOrderDetails }) =>
    // TH 03/09/2017 -- we'll also set PJO.Vessel and PJO.Length when logging pilot aboard.
    // So check CanChangeVessel in addition to CanEditTimes.
    // This is really just defensive since the two permissions currently check the same roles.
    m.Model.CanEditTimes() && m.Model.CanChangeVessel() && !m.JobOrder.Model.Cancellation() && !m.JobOrder.Model.Start.Actual() ?
        <div className="log-start-actual">
            <a href="" onClick={noDefault(() => m.JobOrder.ShowLogActualTimePopup('start-actual'))}>
                <span className="mvmt-in">Log Pilot Aboard</span>
                <span className="mvmt-out">Log Last Line</span>
                <span className="mvmt-shift">Log Last Line</span>
            </a>
        </div> : null
);

const LogEndActualLink = SuperCritical(({ model: m } : { model : PilotJobOrderDetails }) =>
    m.Model.CanEditTimes() && !m.JobOrder.Model.Cancellation() && m.JobOrder.Model.Start.Actual() && !m.JobOrder.Model.End.Actual() ?
        <div className="log-end-actual">
            <a href="" onClick={noDefault(() => m.JobOrder.ShowLogActualTimePopup('end-actual'))}>Log Pilot Complete</a>
        </div> : null
);

const UndoStartActualConfirmation = SuperCritical(({ model: m } : { model : PilotJobOrderDetails }) =>
    m.JobOrder.Model.Start.Actual() && m.JobOrder.ShowUndoStartActual() ?
        <div className="undo-conf-container undo-start-actual">
            <div className="undo-conf-main-line">
                <span className="undo-conf-prompt">
                    <span className="mvmt-in">Undo Log Pilot Aboard?</span>
                    <span className="mvmt-out">Undo Log Last Line?</span>
                    <span className="mvmt-shift">Undo Log Last Line?</span>
                </span>
                <span className="undo-conf-links">
                    <a href="" className="undo-conf-confirm-link" onClick={noDefault(m.JobOrder.UndoStartActual)}>Yes</a>
                    <a href="" className="undo-conf-cancel-link"  onClick={noDefault(() => m.JobOrder.ShowUndoStartActual(false))}>No</a>
                </span>
            </div>
            <div className="undo-conf-descr-line pc-text-smalllabel">
                <span className="mvmt-in">This will revert Actual back to Estimated.</span>
                <span className="mvmt-out">This will revert departure back to Estimated.</span>
                <span className="mvmt-shift">This will revert departure back to Estimated.</span>
            </div>
        </div> : null
);

const UndoEndActualConfirmation = SuperCritical(({ model: m } : { model : PilotJobOrderDetails }) =>
    m.JobOrder.Model.End.Actual() && m.JobOrder.ShowUndoEndActual() ?
        <div className="undo-conf-container undo-end-actual">
            <div className="undo-conf-main-line">
                <span className="undo-conf-prompt">Undo Log Pilot Complete?</span>
                <span className="undo-conf-links">
                    <a href="" className="undo-conf-confirm-link" onClick={noDefault(m.JobOrder.UndoEndActual)}>Yes</a>
                    <a href="" className="undo-conf-cancel-link"  onClick={noDefault(() => m.JobOrder.ShowUndoEndActual(false))}>No</a>
                </span>
            </div>
            <div className="undo-conf-descr-line pc-text-smalllabel">
                <span className="mvmt-in">This will revert back to Est. First Line.</span>
                <span className="mvmt-out">This will revert back to Est. Pilot End.</span>
                <span className="mvmt-shift">This will revert back to Est. Pilot End.</span>
            </div>
        </div> : null
);

const PrimaryBerthLabel = SuperCritical(({ model: m } : { model : PilotJobOrderDetails }) =>
    <div className="primary-location-label">
        <span className="mvmt-in">
            <BerthTypeahead point={m.JobOrder.ToPoint} model={m} />
        </span>
        <span className="mvmt-out">
            <BerthTypeahead point={m.JobOrder.FromPoint} model={m} />
        </span>
        <span className="mvmt-shift">
            <BerthTypeahead point={m.JobOrder.ToPoint} model={m} />
        </span>
    </div>
);

const BerthTypeahead = SuperCritical(({ point: pt, model: m } : { point : () => ItineraryPointViewModel | null, model : PilotJobOrderDetails }) =>
    pt() &&
        <TypeaheadInput className="search-filter"
            model={pt()!.BerthTypeahead}
            disabled={!m.Model.CanEditPortCall() || !!m.JobOrder.Model.Cancellation()} />
);

// "Secondary" Location Label, e.g. "from Sea", "to Sea", "to Reed Rd An..."
const SecondaryLocationLabel = SuperCritical(({ model: m } : { model : PilotJobOrderDetails }) =>
    <div className="secondary-location-label pc-text-fixedwidth">
        <span className="mvmt-in">from {m.JobOrder.FromPoint()?.Model.Location()?.Name()}</span>
        <span className="mvmt-shift">from {m.JobOrder.FromPoint()?.Model.Location()?.Name()}</span>
        <MovementOutSecondaryLocationLabel model={m} locationName={m.JobOrder.ToPoint()?.Model.Location()?.Name() ?? null} />
    </div>
);

const MovementOutSecondaryLocationLabel = SuperCritical(({ model: m, locationName } : { model : PilotJobOrderDetails, locationName : string | null }) =>
    m.Model.CanAddShift() && !m.JobOrder.Model.Cancellation() ?
        <a className="mvmt-out" href=""
            onClick={noDefault(() => m.JobOrder.ShowAddShift(true))}>
            to {locationName}
        </a> :
        <span className="mvmt-out">to {locationName}</span>
);

// "Transit" line - Line itself,transit time text, and different arrow head/tail styling for IN vs OUT vs SHIFT
const TransitLine = SuperCritical(({ model: m } : { model : PilotJobOrderDetails }) => {
    const transitText = formatHrMin(m.JobOrder.TransitTime()) + ' transit';
    return (
        <div className="transit-duration-container">
            <div className="transit-duration-arrow"></div>
            <div className="transit-duration-line"></div>
            <div className="transit-duration-text">
                <span className="mvmt-in">{transitText}</span>
                <span className="mvmt-out">{transitText}</span>
                <span className="mvmt-shift">Shift</span>
            </div>
        </div>
    );
});

function formatHrMin(mins : number) {
    const hrs = Math.floor(Math.abs(mins) / 60),
        minsRemainder = Math.abs(mins) % 60,
        signText = mins < 0 ? 'neg.' : '',
        hrsText = hrs ? ' ' + hrs + ' hr' : '',
        minsText = minsRemainder ? ' ' + minsRemainder + ' min' : '';

    return (signText + hrsText + minsText).trim();
}

const BerthReservationDetails = SuperCritical(({ model: m } : { model : PilotJobOrderDetails }) =>
    <div className="details-section berth-reservation-details">
        <BerthReservationDetailsHeader model={m} />
        <BerthReservationDetailsBody model={m} />
    </div>
);

const BerthReservationDetailsHeader = SuperCritical(({ model: m } : { model : PilotJobOrderDetails }) =>
    <div className="details-section-header">
        <div className="details-section-header-side-item">
            <BerthReservationDetailsReservationSelector model={m} />
            <BerthReservationDetailsNonShiftConflictBadge model={m} />
        </div>
        <span className="details-section-title">Berth Details</span>
    </div>
);

const BerthReservationDetailsReservationSelector = SuperCritical(({ model: m } : { model : PilotJobOrderDetails }) =>
    m.JobOrder.FromPoint()?.BerthReservation() &&
    m.JobOrder.ToPoint  ()?.BerthReservation() ?
        <div className="berth-reservation-selection-container">
            <ReservationSelectorTab
                point={m.JobOrder.FromPoint}
                className="berth-reservation-selector-tab-from"
                text="From"
                model={m}
            />
            <span className="berth-reservation-selection-pipe">|</span>
            <ReservationSelectorTab
                point={m.JobOrder.ToPoint}
                className="berth-reservation-selector-tab-to"
                text="To"
                model={m}
            />
        </div> :
        null
);

const ReservationSelectorTab = SuperCritical(({ point: pt, className, text, model: m } : {
    point : () => ItineraryPointViewModel | null,
    className : string,
    text : string,
    model : PilotJobOrderDetails
}) =>
    <span className={cx('berth-reservation-selection-tab', className)}
        onClick={() => m.BerthReservationDetails.SelectedItineraryPoint(pt())}>
        <span className={cx('berth-reservation-selection-tab-text', {
                'berth-reservation-selection-tab-selected': m.BerthReservationDetails.SelectedItineraryPoint() === pt()
            })}>{text}</span>
        <span className="shift-conflict-container">
            {m.ShowConflicts() && <SmallConflictCountBadge conflictCount={() => pt()?.BerthReservation()?.Model.ConflictCount() ?? 0} />}
        </span>
    </span>
);

const BerthReservationDetailsNonShiftConflictBadge = SuperCritical(({ model: m } : { model : PilotJobOrderDetails }) =>
    m.ShowConflicts() &&
    m.JobOrder.FromPoint() &&
    m.JobOrder.ToPoint  () &&
    (!m.JobOrder.FromPoint()!.BerthReservation() || !m.JobOrder.ToPoint()!.BerthReservation()) ?
        <div className="non-shift-conflict-container">
            <SmallConflictCountBadge conflictCount={() =>
                m.BerthReservationDetails.SelectedItineraryPoint()?.BerthReservation()?.Model.ConflictCount() ?? 0
            } />
        </div> : null
);

const BerthReservationDetailsBody = SuperCritical(({ model: m } : { model : PilotJobOrderDetails }) => {
    const pt = m.BerthReservationDetails.SelectedItineraryPoint(),
        res = pt?.BerthReservation();
    return res && pt ?
        <div className="details-section-body berth-reservation-details-body">
            <div className="selected-reservation-location details-section-title">
                {pt.Model.Location()?.Name()}
            </div>
            <div className="selected-reservation-conflict-text pjo-conflict-msg">
                {m.ShowConflicts() && <IssuesShortSummary issues={res.Model.Issues} />}
            </div>
            <FootmarkAssignmentsView model={res.FootmarkAssignments} />
        </div> :
        null;
});

const ServicesView = SuperCritical(({ model: m } : { model : PilotJobOrderDetails }) =>
    <div className="details-section pjo-services">
        <div className="details-section-header">
            <span className="details-section-title">Services</span>
        </div>
        <div className="details-section-body">
            <TugsView model={m} />
            <LineHandlersView model={m} />
            <LaborView model={m} />
        </div>
    </div>
);

const TugsView = SuperCritical(({ model: m } : { model : PilotJobOrderDetails }) =>
    <div className="service-row">
        <label className="service-row-label pc-text-smalllabel">Tugs</label>
        {m.Model.CanEditServices() && !m.JobOrder.Model.Cancellation() ?
            <span>
                <select className="pc-select service-main-select pjo-select"
                    value={m.JobOrder.Model.TugCompanyId() === null ? '' : '' + m.JobOrder.Model.TugCompanyId()}
                    onChange={e => m.JobOrder.Model.TugCompanyId(e.currentTarget.value === '' ? null : +e.currentTarget.value)}>
                    <option value="" key="">--</option>
                    {m.TugCompanies().map(tug =>
                        <option value={tug.Id()} key={tug.Id()}>{tug.Name()}</option>
                    )}
                </select>
                <span className="pc-select-chevron"></span>
            </span> :
            <span className="service-main-select pc-text-fixedwidth">
                {m.JobOrder.Model.TugCompany()?.Name()}
            </span>
        }
        {m.JobOrder.Model.TugCompanyId() !== null &&
            <div className="service-last-item">
                <label className="service-second-label pc-text-smalllabel">Assigned</label>
                {m.Model.CanEditServices() && !m.JobOrder.Model.Cancellation() ?
                    <NumericInput className="tugs-assigned numeric-input"
                        value={m.JobOrder.Model.NumberOfTugs} /> :
                    <span className="tugs-assigned">{m.JobOrder.Model.NumberOfTugs()}</span>
                }
            </div>
        }
    </div>
);

const LineHandlersView = SuperCritical(({ model: m } : { model : PilotJobOrderDetails }) =>
    <div className="service-row">
        <label className="service-row-label pc-text-smalllabel">Line H.</label>
        {m.Model.CanEditServices() && !m.JobOrder.Model.Cancellation() ?
            <span>
                <select className="pc-select service-main-select pjo-select"
                    value={m.JobOrder.Model.LineHandlingCompanyId() === null ? '' : '' + m.JobOrder.Model.LineHandlingCompanyId()}
                    onChange={e => m.JobOrder.Model.LineHandlingCompanyId(e.currentTarget.value === '' ? null : +e.currentTarget.value)}>
                    <option value="" key="">--</option>
                    {m.LineHandlingCompanies().map(lh =>
                        <option value={lh.Id()} key={lh.Id()}>{lh.Name()}</option>
                    )}
                </select>
                <span className="pc-select-chevron"></span>
            </span> :
            <span className="service-main-select pc-text-fixedwidth">
                {m.JobOrder.Model.LineHandlingCompany()?.Name()}
            </span>
        }
    </div>
);

const LaborView = SuperCritical(({ model: m } : { model : PilotJobOrderDetails }) =>
    <div className="service-row">
        <label className="service-row-label pc-text-smalllabel">Labor</label>
        <NullableDateTimeEditPopupTrigger
            value={m.JobOrder.Model.LaborStartTime}
            disabled={!m.Model.CanEditServices() || !!m.JobOrder.Model.Cancellation()}
            onTriggerPopup={() => m.JobOrder.ShowNullableDateTimeEditPopup('labor-start')}
            localization={m.Localization}
        />
    </div>
);

const DetailsBodyRight = SuperCritical(({ model: m } : { model : PilotJobOrderDetails }) =>
    <div className="details-body-right details-body-column">
        <VesselDetails model={m} />
        <MovementDrafts model={m} />
        <BowThrusterFrontView model={m} />
        <AttendingAgentView model={m} />
        <PilotageChargesFrontView model={m} />
        <div className="space-allocation shiphandlingnotes">
            <ShiphandlingNotesFrontView model={m} />
        </div>
        <MovementRemarks model={m} />
    </div>
);

const VesselDetails = SuperCritical(({ model: m } : { model : PilotJobOrderDetails }) => {
    const v = m.JobOrder.EffectiveVessel();
    return (
        <div className="details-section vessel">
            <div className="details-section-header">
                <span className="details-section-header-side-item pc-text-smalllabel">
                    {v?.Model.Flag()?.Name()}
                </span>
                <span className="details-section-title">
                    {m.JobOrder.Model.DisplayVesselIdentifier()}
                    {m.JobOrder.PortCall()?.Unlisted() ? <span> &mdash; UNLISTED</span> : ''}
                </span>
            </div>
            <div className="details-section-body">
                <div className="vessel-basics-col vessel-basics-col-1">
                    <div className="vessel-item-row">
                        <label className="vessel-item-label pc-text-smalllabel">Length Overall</label>
                        <span className="vessel-item-value pc-text-smalltext pc-text-fixedwidth">
                            {/* TH 03/09/2017 -- Before the switch to persist a PJO Vessel and Length, we used
                                to display the vessel length here, if any, and then fallback to the PortCall Length.
                                I'm now gonna use the PJO EffectiveLength solely.
                                Figure if the Vessel later has its length edited, this will reflect the Length
                                recorded on the PJO, which is also what we use for billing. */}
                            {m.JobOrder.EffectiveLength()}
                        </span>
                    </div>
                    <div className="vessel-item-row">
                        <label className="vessel-item-label pc-text-smalllabel">Beam</label>
                        <span className="vessel-item-value pc-text-smalltext pc-text-fixedwidth">
                            {v?.Beam()}
                        </span>
                    </div>
                    <div className="vessel-item-row">
                        <label className="vessel-item-label pc-text-smalllabel">Gross Tonnage</label>
                        <span className="vessel-item-value pc-text-smalltext pc-text-fixedwidth">
                            {v?.Model.GrossTonnage.toString({ units: false})}
                        </span>
                    </div>
                </div>
                <div className="vessel-basics-col">
                    <div className="vessel-item-row">
                        <label className="vessel-item-label pc-text-smalllabel">IMO</label>
                        <span className="vessel-item-value pc-text-smalltext pc-text-fixedwidth">
                            {v?.Model.IMO()}
                        </span>
                    </div>
                    <div className="vessel-item-row">
                        <label className="vessel-item-label pc-text-smalllabel">Callsign</label>
                        <span className="vessel-item-value pc-text-smalltext pc-text-fixedwidth">
                            {v?.Model.CallSign()}
                        </span>
                    </div>
                    <div className="vessel-item-row">
                        <label className="vessel-item-label pc-text-smalllabel">Vessel Type</label>
                        <span className="vessel-item-value pc-text-smalltext pc-text-fixedwidth">
                            {v?.Model.VesselType()}
                        </span>
                    </div>
                </div>
                <NextLastPortOfCall model={m} />
            </div>
        </div>
    );
});

const MovementDrafts = SuperCritical(({ model: m } : { model : PilotJobOrderDetails }) =>
    <div className="details-section details-section-narrow vessel-drafts">
        <MovementDraftsHeader model={m} />
        <div>
            <DraftEditRow model={m}
                label="Air Draft"
                draft={m.JobOrder.EffectiveAirDraft} />
            <DraftEditRow model={m}
                label="Deep Draft"
                draft={m.JobOrder.EffectiveDeepDraft} />
            <DeepDraftConfirmation model={m} />
        </div>
    </div>
);

const MovementDraftsHeader = SuperCritical(({ model: m } : { model : PilotJobOrderDetails }) =>
    <div className="vessel-draft-header details-section-header details-subsection-header">
        {m.ShowConflicts() && m.JobOrder.HasDraftConflict() &&
            <span className="details-section-header-side-item pjo-conflict-msg">
                Exceeds movement draft
            </span>
        }
        <div className="details-section-title details-subsection-title">Actual Drafts</div>
    </div>
);

const DraftEditRow = SuperCritical(({ model: m, draft, label } : {
    model : PilotJobOrderDetails,
    draft : EffectiveDraftViewModel,
    label : string
}) =>
    <div className={cx('vessel-draft-row', { 'has-default-value': !draft.HasLockedValue() })}>
        <span className="vessel-draft-label pc-text-smalllabel">{label}</span>
        <FeetInchesMetersEditView
            model={draft.FeetInchesMeters}
            disabled={!m.Model.CanEditDrafts() || !!m.JobOrder.Model.Cancellation()}
            onBlur={draft.OnBlur}
        />
    </div>
);

const DeepDraftConfirmation = SuperCritical(({ model: m } : { model : PilotJobOrderDetails }) => {
    const className=cx('pjo-details-confirmation', {
            'pjo-details-confirmed': m.JobOrder.DeepDraftConfirmed()
        }),
        classSpan = '',
        confirmationText = m.JobOrder.DeepDraftConfirmed() ? 'Confirmed' : 'Tentative';
    return (
        <div className="vessel-draft-row deep-draft-row">
            <span className="deep-draft-confirmation">
                {m.Model.CanEditDeepDraftConfirmed() && !m.JobOrder.Model.Cancellation() ?
                    <a href="" className={className}
                        onClick={noDefault(m.JobOrder.ToggleDeepDraftConfirmed)}>
                        {confirmationText}
                    </a> :
                    <span className={classSpan}>{confirmationText}</span>
                }
            </span>
            <span className="vessel-draft-separator"></span>
        </div>
    );
});

const BowThrusterFrontView = SuperCritical(({ model: m } : { model : PilotJobOrderDetails }) =>
    <div className="details-section details-section-narrow bow-thruster-front">
        <BowThrusterFrontHeader model={m} />
        <BowThrusterFrontBody model={m} />
    </div>
);

const BowThrusterFrontHeader = SuperCritical(({ model: m } : { model : PilotJobOrderDetails }) =>
    <div className="details-section-header details-subsection-header">
        {m.JobOrder.EffectiveVessel() &&
            // TH 02/05/2017 -- Permissions:
            //   CanEditVessel - matches server requirement for editing BowThruster fields.
            //   CanEditPortCall -- matches server requirement for editing Next/Last Port of Call
            // Figure you should be able to see the flip as long as you can edit at least one of
            // the items on it.
            (m.Model.CanEditVessel() || m.Model.CanEditPortCall()) &&
            <a href="" className="details-section-header-side-item flip-icon icon-flip-to-more"
                onClick={noDefault(() => m.FlipToBack(BackPage.Vessel))}></a>
        }
        <div className="details-section-title details-subsection-title">Bow Thruster</div>
    </div>
);

const BowThrusterFrontBody = SuperCritical(({ model: m } : { model : PilotJobOrderDetails }) => {
    const v = m.JobOrder.EffectiveVessel();
    return v &&
        <div className="details-section-body">
            {v.BowThruster.Raw.Present() ?
                <div>
                    <BowThrusterVesselSummary vessel={v} />
                    <BowThrusterOperational model={m} />
                </div> :
                <div className="no-bow-thruster">
                    {v.BowThruster.Raw.Present() === null ? 'Unknown' : 'None'}
                </div>
            }
        </div>;
});

const BowThrusterVesselSummary = SuperCritical(({ vessel: v } : { vessel : VesselViewModel }) =>
    <div className="bow-thruster-row">
        <span className="pc-text-label-color">Size</span>
        <span className="bow-thruster-value bow-thruster-size">
            {v.BowThruster.Raw.Size.UOM() === UOM.Horsepower ?
                <span>{v.BowThruster.SizeInHorsepower.toString()}</span> :
                <span>{v.BowThruster.SizeInKilowatts .toString()}</span>
            }
        </span>
    </div>
);

const BowThrusterOperational = SuperCritical(({ model: m } : { model : PilotJobOrderDetails }) =>
    <div className="bow-thruster-row">
        <span className="bow-text pc-text-label-color">Does it work?</span>
        <span className="bow-thruster-value">
            {m.Model.CanEditBowThrusterOperational() && !m.JobOrder.Model.Cancellation() ?
                <YesNoUnknownSelector
                    value={m.JobOrder.Model.BowThrusterOperational}
                    model={m} /> :
                <span>
                    {
                        m.JobOrder.Model.BowThrusterOperational() ? 'Yes' :
                        m.JobOrder.Model.BowThrusterOperational() === null ? '--' :
                        'No'
                    }
                </span>
            }
        </span>
    </div>
);

const ShiphandlingNotesFrontView = SuperCritical(({ model: m } : { model : PilotJobOrderDetails }) =>
    // TH 10/20/2017 -- Changing to make visible to all users who land here.
    // Still need CanAddShiphandlingNote to see the widget for adding notes.
    <div className="details-section details-section-narrow shiphandling-notes-front">
        <ShiphandlingNotesFrontHeader model={m} />
        <ShiphandlingNotesFrontBody model={m} />
    </div>
);

const ShiphandlingNotesFrontHeader = SuperCritical(({ model: m } : { model : PilotJobOrderDetails }) =>
    <div className="details-section-header">
        {m.JobOrder.EffectiveVessel() &&
            <a href="" className="details-section-header-side-item flip-icon icon-flip-to-more"
                onClick={noDefault(() => m.FlipToBack(BackPage.ShiphandlingNotes))}></a>
        }
        <span className="details-section-title">Pilot Shiphandling Notes</span>
    </div>
);

// TH 02/03/2017 -- Max number of shiphandling notes displayed in the front view.
// The full list of notes is displayed on the back flip.
const MAX_NOTES = 2;

const ShiphandlingNotesFrontBody = SuperCritical(({ model: m } : { model : PilotJobOrderDetails }) => {
    const notes = m.JobOrder.EffectiveVessel()?.PilotShiphandlingNotes() ?? [];
    return (
        <div className="details-section-body">
            {/* TH: NOTE. There was a Chrome bug around updating this list when adding a Shiphandling Note from the back flip.
                I removed some unnecessary z-indexes, which provided a workaround. There's still a noticeable lag (~.5 sec) updating this list on Chrome after adding a Shiphandling Note.
                But that's much better than before, when it might take a minute to update. */}
            <div>
                {notes.slice(0, MAX_NOTES).map(n =>
                    <ShiphandlingNoteFrontItem note={n} model={m} key={n.Id()} />
                )}
            </div>
            {notes.length > MAX_NOTES &&
                <div className="shiphandling-notes-more-link-container">
                    <span className="shiphandling-notes-more-link pc-text-smalllabel"
                        onClick={() => m.FlipToBack(BackPage.ShiphandlingNotes)}>...more...</span>
                </div>
            }
        </div>
    );
});

const ShiphandlingNoteFrontItem = SuperCritical(({ note: n, model: m } : { note : PilotShiphandlingNote, model : PilotJobOrderDetails }) =>
    <div className="shiphandling-note">
        <div className="shiphandling-note-main-line">
            <div className="shiphandling-note-left">
                <span className="shiphandling-note-user-name">
                    {n.Noter()?.FullName()}
                </span>
            </div>
            <div className="shiphandling-note-message">
                {n.Message()}
            </div>
        </div>
        <div className="shiphandling-note-timestamp-line">
            <ShiphandlingNoteTimestamp note={n} model={m} />
        </div>
    </div>
);

const AttendingAgentView = SuperCritical(({ model: m } : { model : PilotJobOrderDetails }) => {
    const agent = m.JobOrder.PortCall()?.Agent();
    return agent ?
        <div className="details-section attending-agent">
            {/* TH: Link to agent profile card? Anything else? */}
            <div className="agent-header details-section-header details-section-title">Attending Agent</div>
            <div className="agent-body details-section-body">
                <img className="agent-profile-image" src={agent.Model.Image.ThumbUrl()} />
                {' '}
                <span className="agent-name pc-text-fixedwidth">{agent.Model.FullName()}</span><br />
                <span className="agent-organization-name pc-text-smalllabel pc-text-fixedwidth">
                    {agent.Model.Organization()?.Name()}
                </span><br />
                {/* TH: PhoneNumbers -- More? Show Kind? List other multiple numbers (possibly in tooltip or popup)? */}
                <span className="agent-phone-number pc-text-fixedwidth">
                    {agent.MainNumber()?.Number()}
                </span>
            </div>
        </div> :
        null;
});

const PilotageChargesFrontView = SuperCritical(({ model: m } : { model : PilotJobOrderDetails }) =>
    !m.Model.CanManageBills() ? null :
        <div className="details-section pilotage-charges-front">
            <div className="details-section-header">
                <a href="" className="details-section-header-side-item flip-icon icon-flip-to-more"
                    onClick={noDefault(() => m.FlipToBack(BackPage.PilotageCharges))}></a>
                <span className="details-section-title">Pilotage Charges</span>
            </div>
            <div className="details-section-body pilotage-charges-watermark">
                {
                    m.JobOrder.Model.Bills().length > 0 ? 'Exported' :
                    m.JobOrder.Model.ApprovalOfCharges() !== null ? 'Approved' :
                    'Draft'
                }
            </div>
        </div>
);

const MovementRemarks = SuperCritical(({ model: m } : { model : PilotJobOrderDetails }) =>
    <div className="details-section details-section-narrow pjo-mvmt-remarks">
        <div className="details-section-header">
            {m.JobOrder.Model.Remarks() &&
                <a href="" className="details-section-header-side-item"
                    onClick={noDefault(m.JobOrder.ToggleRemarksVisible)}>
                    {m.JobOrder.EffectiveUserPreferences.RemarksVisible() ? 'Hide': 'Show'}
                </a>
            }
            <span className="details-section-title">Movement Remarks</span>
        </div>
        <div className="details-section-body pjo-mvmt-remarks-body">
            <textarea className="pjo-mvmt-remarks-content"
                placeholder="Enter remarks ..."
                value={m.JobOrder.Model.Remarks() || ''}
                onChange={e => m.JobOrder.Model.Remarks(e.currentTarget.value)}
                // Can edit remarks even for CNX PJOs
                disabled={!m.Model.CanEdit()}></textarea>
        </div>
    </div>
);

const DetailsBackContent = SuperCritical(({ model: m } : { model : PilotJobOrderDetails }) =>
    <div className={cx('face back', { flipped: m.Flipped() })}>
        <MovementLog model={m} />
        <ShiphandlingNotesFlipView model={m} />
        <VesselFlipView model={m} />
        <PilotageChargesView model={m.PilotageCharges} backPage={m.BackPage} flipToFront={() => m.Flipped(false)} />
    </div>
);

const MovementLog = SuperCritical(({ model: m } : { model : PilotJobOrderDetails }) =>
    m.BackPage() === BackPage.Log ?
        <div className="mvmt-log">
            <div className="flip-back-link-container">
                <a href="" className="flip-icon icon-flip-to-main"
                    onClick={noDefault(() => m.Flipped(false))}></a>
            </div>
            <PilotJobOrderLogView model={m.Log} />
        </div> : null
);

const ShiphandlingNotesFlipView = SuperCritical(({ model: m } : { model : PilotJobOrderDetails }) =>
    m.BackPage() === BackPage.ShiphandlingNotes ?
        <div className="shiphandling-notes-full">
            <div className="details-section-header">
                <a href="" className="details-section-header-side-item flip-icon icon-flip-to-main"
                    onClick={noDefault(() => m.Flipped(false))}></a>
                <span className="details-section-title">Pilot Shiphandling Notes</span>
            </div>
            <ShiphandlingNotesFlipBody model={m} />
        </div> : null
);

const ShiphandlingNotesFlipBody = SuperCritical(({ model: m } : { model : PilotJobOrderDetails }) =>
    <div className="shiphandling-notes-full-body">
        <AddNoteView model={m} />
        <div>
            {m.JobOrder.EffectiveVessel()?.PilotShiphandlingNotes().map(n =>
                <ShiphandlingNotesFlipItem note={n} model={m} key={n.Id()} />
            )}
        </div>
    </div>
);

const AddNoteView = SuperCritical(({ model: m } : { model : PilotJobOrderDetails }) => {
    // Can add shiphandling notes, even for CNX PJOs. Note that remarks are added to whichever
    // vessel was selected at time of cancellation.
    if (!m.Model.CanAddShiphandlingNote()) return null;
    const v = m.JobOrder.EffectiveVessel();
    return v &&
        <form className="shiphandling-note draft-note">
            <img className="shiphandling-note-profile-img"
                src={m.User()?.Image.ThumbUrl()} />
            <input className="draft-note-input" placeholder="Add Pilot Note"
                value={v.DraftNote().Message() ?? ''}
                onChange={e => v.DraftNote().Message(e.currentTarget.value)} />
            <button className="pc-button draft-note-button"
                onClick={noDefault(v.AddShiphandlingNote)}>Add</button>
        </form>;
});

const ShiphandlingNotesFlipItem = SuperCritical(({ note: n, model: m } : { note : PilotShiphandlingNote, model : PilotJobOrderDetails }) =>
    <div className="shiphandling-note">
        <div className="shiphandling-note-left">
            <img className="shiphandling-note-profile-img"
                src={n.Noter()?.Image.ThumbUrl()} />
            <span className="shiphandling-note-user-name">
                {n.Noter()?.AbbreviatedName()}
            </span>
        </div>
        <div className="shiphandling-note-message">
            <span>{n.Message()}</span>
            <ShiphandlingNoteTimestamp note={n} model={m} />
        </div>
    </div>
);

const ShiphandlingNoteTimestamp = SuperCritical(({ note: n, model: m } : { note : PilotShiphandlingNote, model : PilotJobOrderDetails }) =>
    /* TH: NOTE: In theory, Shiphandling Notes might come from other Domains.
        Currently, not displaying any timezone info in timestamp tooltip or converting to user's Domain's timezone.
        May wanna do one or the other if we ever have an installation w/ multiple Domains. */
    n.Event() &&
        <TimeAgoTooltip className="shiphandling-note-timestamp pc-text-smalllabel pc-text-fixedwidth-tooltip"
            date={n.Event()!.Date}
            now={m.Clock.now} />
);

const VesselFlipView = SuperCritical(({ model: m } : { model : PilotJobOrderDetails }) =>
    m.BackPage() === BackPage.Vessel ?
        <div className="vessel-flip">
            <div className="vessel-flip-content">
                <BowThrusterFullView model={m} />
            </div>
        </div> : null
);

const BowThrusterFullView = SuperCritical(({ model: m } : { model : PilotJobOrderDetails }) =>
    // Only show if user has permissions to edit BowThruster fields, but show even for CNX PJOs
    m.Model.CanEditVessel() ?
        <div className="bow-thruster-full">
            <div className="details-section-header">
                <a href="" className="details-section-header-side-item flip-icon icon-flip-to-main"
                    onClick={noDefault(() => m.Flipped(false))}></a>
                <span className="details-section-title">Bow Thruster</span>
            </div>
            <BowThrusterFullBody model={m} />
        </div> : null
);

const BowThrusterFullBody = SuperCritical(({ model: m } : { model : PilotJobOrderDetails }) => {
    const b = m.JobOrder.EffectiveVessel()?.BowThruster;
    return b ?
        <div className="details-section-body">
            <div className="bow-thruster-row">
                <span className="bow-thruster-label pc-text-label-color">Present</span>
                {/* TH 02/05/2017 -- TODO: For incorporating BowThruster into Lloyd's --
                    That'll mean users no longer edit vessel.BowThruster directly but
                    instead make they're edits to the OverrideServiceVessel.
                    I'd recommend UI that makes it clear user's are editing an override, say
                    by showing the PortCall value, the Lloyd's value, and the override value,
                    similar to the vessel overrides page. */}
                <YesNoUnknownSelector value={b.Raw.Present} model={m} />
            </div>
            {b.Raw.Present() &&
                <div>
                    <div className="bow-thruster-row">
                        <span className="bow-thruster-label pc-text-label-color">Size</span>
                        <span>
                            <NullableNumericInput className="numeric-input bow-thruster-size"
                                value={b.Raw.Size.UOM() === UOM.Horsepower ? b.SizeInHorsepower.Num : b.SizeInKilowatts.Num}
                                onBlur={b.OnSizeBlur} />
                        </span>
                    </div>
                    <div className="bow-thruster-row">
                        <span className="bow-thruster-label pc-text-label-color">UoM</span>
                        <span>
                            <select className="pc-select pjo-select"
                                value={b.Raw.Size.UOM() || ''}
                                onChange={e => b.Raw.Size.UOM(e.currentTarget.value)}>
                                <option value={UOM.Horsepower}>{UOM.Horsepower}</option>
                                <option value={UOM.Kilowatt  }>{UOM.Kilowatt  }</option>
                            </select>
                            <span className="pc-select-chevron"></span>
                        </span>
                    </div>
                </div>
            }
        </div> :
        null;
})

const NextLastPortOfCall = SuperCritical(({ model: m } : { model : PilotJobOrderDetails }) => {
    const pc = m.JobOrder.PortCall();
    return (
        <div className="ports-of-call">
            <div className="details-section-body">
                <div className="last-port-of-call">
                    <span className="pc-text-smalllabel lbl">Next</span>
                    {pc &&
                        <input type="text" placeholder="Port of Call"
                            value={pc.Model.NextPortOfCall() || ''}
                            onChange={e => pc.Model.NextPortOfCall(e.currentTarget.value)}
                            disabled={!m.Model.CanEditPortCall() || !!m.JobOrder.Model.Cancellation()} />
                    }
                </div>
                <div className="next-port-of-call">
                    <span className="pc-text-smalllabel lbl">Last</span>
                    {pc &&
                        <input type="text" placeholder="Port of Call"
                            value={pc.Model.LastPortOfCall() || ''}
                            onChange={e => pc.Model.LastPortOfCall(e.currentTarget.value)}
                            disabled={!m.Model.CanEditPortCall() || !!m.JobOrder.Model.Cancellation()} />
                    }
                </div>
            </div>
        </div>
    );
});

const YesNoUnknownSelector = SuperCritical(({ value, model: m, className } : {
    value : DataSignal<boolean | null>,
    model : PilotJobOrderDetails,
    className? : string
}) =>
    <span>
        <select className={cx('pc-select pjo-select', className)}
            value={boolToNum(value())}
            onChange={e => value(numToBool(+e.currentTarget.value))}>
            {m.YesNoUnknown.map(({ name, value }) =>
                <option value={boolToNum(value)} key={boolToNum(value)}>{name}</option>
            )}
        </select>
        <span className="pc-select-chevron"></span>
    </span>
);

function boolToNum(val : boolean | null) {
    return val === null ? 0 : val ? 1 : -1;
}

function numToBool(num : number) {
    return num === 0 ? null : num > 0;
}

const CancellationRemarksPromptView = SuperCritical(({ modelContainer } : {
    modelContainer : () => CancellationRemarksPrompt | null
}) => {
    const m = modelContainer();
    return (
        <PCModal shown={() => !!m} className="pilotjoborderdetails-cancellation-remarks-prompt" onBackDropClick={m?.QuitChanges}>
            <div className="modal-content pc-card">
                <div className="modal-body">
                    <h2 className="modal-title">Cancel Pilot Order</h2>
                    <div>
                        Are you sure you want to cancel this pilot order?
                    </div>
                    {m &&
                        <div>
                            <div className="remarks-shell">
                                <textarea
                                    className="remarks"
                                    value={m.Remarks() ?? ''}
                                    onChange={e => m.Remarks(e.currentTarget.value || null)}
                                    placeholder="These remarks will be added to the log."
                                    rows={3}
                                />
                            </div>
                            <div className="actions">
                                <button className="close pc-modal-close close-btn" onClick={m.QuitChanges}>Close</button>
                                <button className="pc-button" onClick={noDefault(m.CancelPJO)}>Cancel</button>
                            </div>
                        </div>
                    }
                </div>
            </div>
        </PCModal>
    );
})

const AbortRemarksPromptView = SuperCritical(({ modelContainer } : {
    modelContainer : () => AbortRemarksPrompt | null
}) => {
    const m = modelContainer();
    return (
        <PCModal shown={() => !!m} className="pilotjoborderdetails-cancellation-remarks-prompt" onBackDropClick={m?.QuitChanges}>
            <div className="modal-content pc-card">
                <div className="modal-body">
                    <h2 className="modal-title">Abort Pilot Order</h2>
                    <div>
                        Are you sure you want to abort this pilot order?
                    </div>
                    {m &&
                        <div>
                            <div className="remarks-shell">
                                <textarea
                                    className="remarks"
                                    value={m.Remarks() ?? ''}
                                    onChange={e => m.Remarks(e.currentTarget.value || null)}
                                    placeholder="These remarks will be added to the log."
                                    rows={3}
                                />
                            </div>
                            <div className="actions">
                                <button className="close pc-modal-close close-btn" onClick={m.QuitChanges}>Close</button>
                                <button className="pc-button" onClick={noDefault(m.AbortPJO)}>Abort</button>
                            </div>
                        </div>
                    }
                </div>
            </div>
        </PCModal>
    );
})

const CancelWholePortCallPromptView = SuperCritical(({ modelContainer } : {
    modelContainer : () => CancelWholePortCallPrompt | null
}) => {
    const m = modelContainer();
    return (
        <PCModal shown={() => !!m} className="pilotjoborderdetails-cancel-whole-portcall-prompt">
            <div className="modal-content pc-card">
                <h2 className="prompt-title">One Last Thing</h2>
                {
                    m?.Status() === CancelWholePortCallStatus.Prompting ||
                    m?.Status() === CancelWholePortCallStatus.Cancelling ?
                        <CancelWholePortCallPromptingView model={m} /> :
                    m?.Status() === CancelWholePortCallStatus.CancellationSucceeded ?
                        <CancelWholePortCallSucceededView model={m} /> :
                    m?.Status() === CancelWholePortCallStatus.CancellationFailed ?
                        <CancelWholePortCallFailedView model={m} /> :
                    null
                }
                <ActionLoadingIndicator loading={() => m?.Status() === CancelWholePortCallStatus.Cancelling} />
            </div>
        </PCModal>
    );
});

const CancelWholePortCallPromptingView = SuperCritical(({ model: m } : { model : CancelWholePortCallPrompt }) =>
    <div className="prompting">
        <div>
            You just cancelled the last pilot order for this PortCall.
            <br />
            Would you like to...
        </div>
        <div className="controls">
            <div className="cancellation-option">
                <div>
                    <button
                        className="pc-button"
                        onClick={m.HidePrompt}
                    >
                        Cancel Pilot Only
                    </button>
                </div>
                <div className="option-explanation">
                    The ship is still coming here but does not require a pilot.
                </div>
            </div>
            <div className="cancellation-option">
                <div>
                    <button
                        className="pc-button"
                        onClick={m.CancelWholePortCall}
                    >
                        Cancel PortCall
                    </button>
                </div>
                <div className="option-explanation">
                    The ship is not coming.
                </div>
            </div>
        </div>
    </div>
);

const CancelWholePortCallSucceededView = SuperCritical(({ model: m } : { model : CancelWholePortCallPrompt }) =>
    <div className="succeeded">
        <div>The PortCall was cancelled successfully.</div>
        <div className="controls">
            <button
                className="pc-button"
                onClick={m.HidePrompt}
            >
                OK
            </button>
        </div>
    </div>
);

const CancelWholePortCallFailedView = SuperCritical(({ model: m } : { model : CancelWholePortCallPrompt }) =>
    <div className="failed">
        <div>
            An unexpected error occurred while cancelling the PortCall.
            Please try cancelling the PortCall from the
            {' '}
            <a href={m.PortCallProfileHref()}>PortCall profile</a>. Details:
        </div>
        <div className="exception-details">
            {m.ExceptionDetails()}
        </div>
        <div className="controls">
            <button
                className="pc-button"
                onClick={m.HidePrompt}
            >
                OK
            </button>
        </div>
    </div>
);

// TH 07/10/2017 -- These are popups that are part of the PJO Details popup but can't live
// inside of the PJO Details DOM b/c of "position: fixed" issues.
// So we'll instead instantiate this view from higher up in the DispatchDashboard DOM.
export const PilotJobOrderDetailsPopupViews = SuperCritical(({ modelContainer } : {
    modelContainer : () => PilotJobOrderDetails | null
}) => {
    const m = modelContainer();
    return m &&
        <div>
            <EditMovementEndPopupView model={m.JobOrder.EditMovementEndPopup} />
            <AddShiftPopupView modelContainer={m.JobOrder.AddShiftPopup} />
            <DateTimeEditPopupView
                modelContainer={m.JobOrder.DateTimeEditPopup}
                localization={m.Localization}
            />
            <DateTimeEditPopupView
                title="Log Actual Time"
                modelContainer={m.JobOrder.LogActualTimePopup}
                localization={m.Localization}
            />
            <NullableDateTimeEditPopupView
                modelContainer={m.JobOrder.NullableDateTimeEditPopup}
                localization={m.Localization}
            />
            <OriginalPilotOrderView model={m.OriginalPilotOrder} />
            <CancellationRemarksPromptView modelContainer={m.JobOrder.CancellationRemarksPrompt} />
            <AbortRemarksPromptView modelContainer={m.JobOrder.AbortRemarksPrompt} />
            <CancelWholePortCallPromptView modelContainer={m.JobOrder.CancelWholePortCallPrompt} />
            <ErrorModal errors={m.AgentRestrictions} shown={m.ShowAgentRestrictions} title="There are issues with your Pilot Order" />
        </div>;
});



// WEBPACK FOOTER //
// ./apps/pilots/pilotjoborderdetails/view.tsx